import React, {useEffect, useState} from "react";
import {BrowserRouter, Redirect, Route, Switch} from "react-router-dom";
import {ConfigProvider, notification} from "antd";
import ruRu from 'antd/es/locale/ru_RU'
import enUS from 'antd/es/locale/en_US';
import zhCN from 'antd/es/locale/zh_CN';
import moment from "moment";
import "moment/locale/ru";
import 'moment/locale/zh-cn';
import "@assets/style.less";

import {AppContext} from "@contexts/app-context";

import Header from "@components/header";
import ScrollNavigation from "@components/scroll-navigation";
import ChooseTariffPage from "../pages/billing-page/choose-tariff";
import Content from "@components/content";
import MainFooter from "@components/main-page-blocks/footer";
import CookieConsent from "@components/cookie-consent";
import CookieConsentProvider from "@components/cookie-consent/provider";
import Error from "@components/error";
import PrivateRoute from "@components/routers/private-router";
import ModalCreateNewLegalEntity from "@modals/create-legal-entity";
import HomePage from "@pages/home-page";
import PlatformNewsPage from "@pages/platform-news-page";
import {defaultPlatformNewsPath} from "../../content/platform-news";
import LoginPage from "@pages/login-page";
import ResetPage from "@pages/reset-page";
import RegisterPage from "@pages/register-page";
import CarrierNewDeliveries from "@pages/deliveries/carrier/new-deliveries";
import CarrierBookedDeliveries from "@pages/deliveries/carrier/booked-deliveries";
import CarrierArchivedDeliveries from "@pages/deliveries/carrier/archived-deliveries";
import LogistFreeDeliveries from "@pages/deliveries/logist/free-deliveries";
import LogistReservedDeliveries from "@pages/deliveries/logist/reserved-deliveries";
import LogistBookedDeliveries from "@pages/deliveries/logist/booked-deliveries";
import LogistArchivedDeliveries from "@pages/deliveries/logist/archived-deliveries";
import DriverNewDeliveries from "@pages/deliveries/driver/new-deliveries";
import LogistOnLoadingDeliveries from "@pages/deliveries/logist/on-load-deliveries";
import LogistEnRouteDeliveries from "@pages/deliveries/logist/enroute-deliveries";
import CarrierOnLoadingDeliveries from "@pages/deliveries/carrier/on-load-deliveries";
import CarrierEnRouteDeliveries from "@pages/deliveries/carrier/enroute-deliveries";
import {cookieConsentService, userServices} from "@services";
import SettingsPage from "@pages/settings-page";
import ReportsPage from "@pages/reports-page";
import RouteRootClass from "./RouteRootClass";
import GuestNewDeliveries from "@pages/deliveries/guest/free-deliveries";
import ConsignorPage from "@pages/consignor-page";
import CompanyPage from "@pages/company-page";
import TermsOfUse from "@pages/terms-of-use";
import ServicePage from "@pages/service-page";
import ReturnDeliveries from "@pages/deliveries/carrier/return-deliveries";
import DriverPage from "@pages/driver-page";
import StartPage from "@pages/start-page";
import PrivacyPage from "@pages/privacy";
import ContactsPage from "@pages/contacts-page";
import {
  getHtmlLanguage,
  LANG_ENUM,
  LANGS,
} from "../../helpers/lang.helper";
import i18n from "../../i18n";
import LoadingUnloadingPage from "../pages/loading-unloading";
import ModalLegalEntityAgreement from "../modals/legal-entity-agreement";
import BuyerPage from "../pages/unloadings/buyer/actual-unloadings/index";
import BuyerArchivedUnloadings from "../pages/unloadings/buyer/archived-unloadings";
import {getTranslatedConstant} from "../../helpers/translate.helpers";
import CrossDockPage from "../pages/cross-dock";

notification.config({
  placement: "bottomRight",
  bottom: 25,
  duration: 5,
});

const USER_INFO_REFRESH_INTERVAL_MS =
    10 * 60 * 1000;

const ANT_DESIGN_LOCALES = Object.freeze({
  [LANG_ENUM.RU]: ruRu,
  [LANG_ENUM.EN]: enUS,
  [LANG_ENUM.CN]: zhCN,
});

const MOMENT_LOCALES = Object.freeze({
  [LANG_ENUM.RU]: "ru",
  [LANG_ENUM.EN]: "en",
  [LANG_ENUM.CN]: "zh-cn",
});

function isSupportedLanguage(language) {
  return LANGS.some(({key}) => key === language);
}

function getLanguageDefinition(language) {
  return LANGS.find(({key}) => key === language);
}

function createLanguageStore(language) {
  const languageDefinition = getLanguageDefinition(language);

  return {
    lang: language,
    locale: languageDefinition.locale,
    langObj: ANT_DESIGN_LOCALES[language],
  };
}

function applyLanguage(language) {
  i18n.changeLanguage(language);
  moment.locale(MOMENT_LOCALES[language]);

  if (typeof document !== "undefined") {
    document.documentElement.lang = getHtmlLanguage(language);
  }
}

function getInitialLanguage() {
  const storedLanguage = cookieConsentService.getFunctionalCookie(
    "language"
  );

  if (isSupportedLanguage(storedLanguage)) {
    return storedLanguage;
  }

  return LANG_ENUM.RU;
}

export default function App() {
  const [user, setUser] = useState(() => userServices.getUser());
  const [showModalCreateEntity, setShowModalCreateEntity] = useState(false);
  const [showModalLegalEntityAgreement, setShowModalLegalEntityAgreement] = useState(false);


  const updateUser = (
      callback = null,
      isNeedSetUser = true
  ) => {
    return userServices
        .updateUserInfo()
        .then((updateResult = {}) => {
          const userData =
              userServices.getUser();

          if (
              isNeedSetUser ||
              updateResult.sessionExpired
          ) {
            setUser(userData);
          }

          if (
              callback instanceof Function
          ) {
            callback(userData);
          }

          return userData;
        });
  };

  /**
   * Автообновление информации о пользователе
   * каждые 10 минут.
   */
  useEffect(() => {
    updateUser();

    const updateInterval =
        setInterval(
            () =>
                updateUser(
                    null,
                    false
                ),
            USER_INFO_REFRESH_INTERVAL_MS
        );

    return () =>
        clearInterval(
            updateInterval
        );
  }, []);

  const handleUserTypeChanged = async () => {
    const userData = await updateUser(null, false);

    const destination = userData.info.isStaff
        ? "/deliveries/list-free"
        : "/deliveries/list-new";

    window.location.replace(destination);
  };

  useEffect(() => {
    if (user && user.loggedIn) {
      if (user.info.rules.IsNeedCreateLegalEntity) {
        setShowModalLegalEntityAgreement(true);
      }
    }
  }, [user])

  const AppProvider = (props) => {
    const userData = userServices.getUser();

    const [userStore, setUserStore] = useState({
      ...userData,
    });

    const [currentPath, setCurrentPath] = useState(null);

    const [langStore, setLangStore] = useState(() => {
      const initialLanguage = getInitialLanguage();

      applyLanguage(initialLanguage);

      return createLanguageStore(initialLanguage);
    });

    let defaultSort = getTranslatedConstant("defaultSort");

    const [filterStore, setFilterStore] = useState({
      enable: false,
      selectedRegionsLoading: {},
      selectedRegionsUnloading: {},
      selectedSorting: defaultSort[0].name,
      regionsLoadings: [],
      regionsUnloadings: [],
      sort: defaultSort,
      selectedConsignors: [],
      excludedConsignors: [],
      isExcludeConsignors: false,
    });

    useEffect(() => {
      const translatedDefaultSort = getTranslatedConstant("defaultSort");

      setFilterStore((currentFilterStore) => ({
        ...currentFilterStore,
        selectedSorting: translatedDefaultSort[0].name,
        sort: translatedDefaultSort,
      }));
    }, [langStore.lang]);

    const [isNeedUpdateUnloadingList, setNeedUpdateUnloadingList] = useState(false);


    const stores = {
      currentPath: currentPath,
      user: userStore,
      filter: {
        ...filterStore,
      },
      lang: {...langStore},
      isNeedUpdateUnloadingList: isNeedUpdateUnloadingList
    };

    const appStoreProvider = {
      ...stores,
      applyFilterParams: (selectedRegionsLoading, selectedRegionsUnloading, selectedSorting, selectedConsignors, excludedConsignors, isExcludeConsignors) => {
        setFilterStore({
          ...filterStore,
          enable: true,
          selectedRegionsLoading: selectedRegionsLoading,
          selectedRegionsUnloading: selectedRegionsUnloading,
          selectedSorting: selectedSorting,
          selectedConsignors: selectedConsignors,
          excludedConsignors: excludedConsignors,
          isExcludeConsignors: isExcludeConsignors
        });
      },
      applySearchString: (searchString) => {
        setFilterStore({
          ...filterStore,
          enableSearch: true,
          searchString: searchString
        });
      },
      initFilter: (listSort = defaultSort) => {
        setFilterStore({
          ...filterStore,
          sort: listSort,
        });
      },
      enableFilter: (regionsLoadings, regionsUnloadings) => {
        setFilterStore({
          ...filterStore,
          enable: true,
          regionsLoadings: regionsLoadings,
          regionsUnloadings: regionsUnloadings,
        });
      },
      enableSearchFilter: () => {
        setFilterStore({
          ...filterStore,
          enableSearch: true,
        });
      },
      disableFilter: () => {
        setFilterStore({
          ...filterStore,
          enable: false,
          regionsLoadings: [],
          regionsUnloadings: [],
          selectedRegionsLoading: [],
          selectedRegionsUnloading: [],
          selectedSorting: defaultSort[0].name,
          selectedConsignors: [],
          excludedConsignors: [],
          isExcludeConsignors: false
        });
      },
      login: async (username, password) => {
        return await userServices.login(username, password).then((resUserData) => {
          userServices.saveUser(resUserData);

          const userData = userServices.getUser();
          setUserStore({...userData});

          updateUser();
        });
      },
      logout: () => {
        userServices.logout().then(() => {
          const userData = userServices.getUser();

          setFilterStore({...filterStore, enable: false});
          setUserStore({...userData});

          updateUser();
        });
      },
      reset: async (username) => {
        return await userServices.resetPass(username).then(() => {
          return username;
        });
      },
      setLang: (language) => {
        if (!isSupportedLanguage(language)) {
          return;
        }

        applyLanguage(language);

        cookieConsentService.setFunctionalCookie(
          "language",
          language
        );

        setLangStore(createLanguageStore(language));
      },
      setIsNeedUpdateUnloadingList: (value) => {
        setNeedUpdateUnloadingList(value);
      },
      setCurrentPath: (path) => {
        setCurrentPath(path)
      }
    };

    return <AppContext.Provider value={appStoreProvider}>
      <ConfigProvider locale={langStore.langObj}>{props.children}
      </ConfigProvider></AppContext.Provider>;
  };

  return (
    <AppProvider>
      <BrowserRouter>
        <CookieConsentProvider>
          <RouteRootClass/>

          <div className="wrapper">
            <Header
              user={user}
              onUserTypeChanged={handleUserTypeChanged}
            />

            <ScrollNavigation/>
          <Switch>
            <Route path="/" exact component={HomePage}/>
            <Route path="/login" component={LoginPage} user={user}/>
            <Route path="/register" component={RegisterPage}/>
            <Route path="/reset" component={ResetPage}/>
            <Route path="/terms-of-use" component={TermsOfUse}/>
            <Route path="/privacy-page" component={PrivacyPage}/>
            <Route path="/contacts" exact component={ContactsPage}/>

            <Redirect exact from="/news" to={defaultPlatformNewsPath}/>
            <Route path="/news/:newsSlug" component={PlatformNewsPage}/>

            <Route path="/deliveries/dashboard-deliveries">
              <Content>
                <GuestNewDeliveries/>
              </Content>
            </Route>

            <Route path="/service" render={() => (
                <Content>
                  <ServicePage/>
                </Content>
            )}
            />

            <PrivateRoute path="/deliveries/list-new" component={<CarrierNewDeliveries/>}/>
            <PrivateRoute path="/deliveries/return-deliveries/:id" component={<ReturnDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-my" component={<CarrierBookedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-archived-carrier" component={<CarrierArchivedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-onload-carrier" component={<CarrierOnLoadingDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-enroute-carrier" component={<CarrierEnRouteDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-free" component={<LogistFreeDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-reserve" component={<LogistReservedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-booked" component={<LogistBookedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-archived-logist" component={<LogistArchivedDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-onload-logist" component={<LogistOnLoadingDeliveries/>}/>
            <PrivateRoute path="/deliveries/list-enroute-logist" component={<LogistEnRouteDeliveries/>}/>
            <PrivateRoute path="/unloadings/list-actual" component={<BuyerPage/>}/>
            <PrivateRoute path="/unloadings/list-archived" component={<BuyerArchivedUnloadings/>}/>
            <PrivateRoute path="/loading-unloading/" component={<LoadingUnloadingPage/>}/>
            <PrivateRoute path="/cross-dock/list-active" component={<CrossDockPage type="active" />}/>
            <PrivateRoute path="/cross-dock/list-archive" component={<CrossDockPage type="archive" />}/>
            <PrivateRoute path="/deliveries/list-new-driver" component={<DriverNewDeliveries/>}/>
            <PrivateRoute path="/settings" component={<SettingsPage/>}/>
            <PrivateRoute path="/reports" component={<ReportsPage/>} aside={true}/>
            <PrivateRoute path="/driver" component={<DriverPage/>}/>
            <PrivateRoute path="/company" component={<CompanyPage/>}/>
            <PrivateRoute path="/consignor" component={<ConsignorPage/>}/>
            <PrivateRoute path="/start" component={<StartPage/>}/>

            <PrivateRoute path="/billing/choose-tariff" component={<ChooseTariffPage/>}/>

            <Route render={() => <Content><Error status={404} desc={i18n.t('main.status.404')}/></Content>}/>
          </Switch>
          <MainFooter/>
        </div>
        {/*<AdsBanner type={"1"}/>*/}
        <ModalLegalEntityAgreement
          isShow={showModalLegalEntityAgreement}
          setIsShow={setShowModalLegalEntityAgreement}
          onAccepted={() => setShowModalCreateEntity(true)}
        />
        <ModalCreateNewLegalEntity
          isShow={showModalCreateEntity}
          setIsShow={setShowModalCreateEntity}
          setLoadingList={() => {
          }}
          setStopListUpdate={() => {
          }}
        />

          <CookieConsent/>
        </CookieConsentProvider>
      </BrowserRouter>
    </AppProvider>
  );
}
